/* Customers, and one customer's page.
 *
 * These are real records — the office adds and edits them, and the server keeps
 * a customer from being entered twice (see `services/customers.ts`).
 *
 * The list is **paged, searched and ordered on the server**, the way the jobs
 * list is: it is every customer the shop has ever sprayed for, and it only
 * grows. The counts on each row come with it — how many of their jobs are in
 * the shop, how many components that is, and the next promise outstanding —
 * because the list can be put in order of that date, and a column ordered by
 * the server's answer but drawn from the client's is how a list gets accused of
 * sorting wrong.
 *
 * The whole customer list is still loaded as well, and still has to be: the job
 * form's picker searches all of them and the duplicate check compares against
 * all of them. That is `st.custs`; this screen reads `st.custList`.
 *
 * Subcontractors don't get here. A subbie who can see "Henderson Joinery, 400
 * doors, promised Friday" has the shop's customer list — so the list screen
 * renders a plain explanation instead, the server refuses them the data behind
 * it, and the job detail drops the customer link for them too. */
import { useEffect, type CSSProperties } from 'react';
import { areaFor } from '@/domain/items';
import { isOverdue, overdueDay, scheduled } from '@/domain/schedule';
import { compareDays, formatDay } from '@/domain/time';
import {
  currentCustomer,
  isOffice,
  seesCustomers,
  stageName,
  stageOf,
  useApp,
} from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { BackButton, Empty, PageHead, Pager, Panel, PanelHead, SortHeader } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import { useNarrowerThan, usePageGutter, withGutter } from '@/ui/viewport';
import type { CustomerSort } from '@/types';

/* Below this a customer's page is one column. The width is the two panels'
 * own — the jobs list and the summary beside it — plus the page's padding and
 * the gap, not an iPad. */
const CUSTOMER_TWO_COL = 860;

const typeTag = (trade: boolean): CSSProperties => ({
  fontFamily: 'var(--font-heading)',
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  padding: '3px 8px',
  borderRadius: 999,
  background: trade ? 'var(--accent-tint)' : 'var(--line-soft)',
  color: trade ? 'var(--accent-deep)' : 'var(--ink-muted)',
});

/** A field nobody has filled in reads as an em dash on the screen. The record
 *  holds null, so two customers with no phone number are not the same one. */
const orDash = (value: string | null) => value ?? '—';

/** The two columns the list can be put in order of, and how each one reads
 *  once it is. Said in words rather than left to the caret alone — the caret is
 *  the second half of it, and a caption nobody has to decode is the first. */
const SORTABLE: Record<CustomerSort, { heading: string; asc: string; desc: string }> = {
  name: { heading: 'Customer', asc: 'A to Z', desc: 'Z to A' },
  promised: {
    heading: 'Next promised',
    asc: 'soonest promise first',
    desc: 'furthest promise first',
  },
};

/** A sortable heading, wired to the store. The heading, the caret and the
 *  `aria-sort` are the jobs list's — see `ui/controls.tsx`. */
function CustSortHeader({ column, style }: { column: CustomerSort; style?: CSSProperties }) {
  const sort = useApp((s) => s.custSort);
  const dir = useApp((s) => s.custDir);
  const sortCustomersBy = useApp((s) => s.sortCustomersBy);
  const on = sort === column;
  const { heading, asc, desc } = SORTABLE[column];

  return (
    <SortHeader
      heading={heading}
      on={on}
      dir={dir}
      onSort={() => sortCustomersBy(column)}
      title={on ? `Sort ${dir === 'asc' ? desc : asc}` : `Sort ${asc}`}
      style={style}
    />
  );
}

const num: CSSProperties = {
  textAlign: 'right',
  fontFamily: 'var(--font-data)',
  fontSize: 13,
};

export function CustomersScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const visible = seesCustomers(st);
  const office = isOffice(st);
  const loadCustomerList = st.loadCustomerList;

  // The page is a request, so opening the screen makes one. Every other way it
  // changes — a heading, the search box, the pager — asks again from the store,
  // because each of those is a different question for the server.
  useEffect(() => {
    // The role switch in the account menu is a preview: as a previewed
    // subcontractor the office would get a 403, so don't ask.
    if (visible) void loadCustomerList();
  }, [visible, loadCustomerList]);

  const rows = st.custList;
  const q = st.custQ.trim();
  // Jobs in the shop across every customer, not just this page — the board's
  // working set is the whole shop, so this stays true as the pages turn.
  const inShop = st.jobs.filter((j) => !stageOf(st, j.stage)?.terminal);
  const order = SORTABLE[st.custSort][st.custDir];

  return (
    /* The same frame the jobs list uses, and just as easy to undo by accident:
       `Shell`'s <main> is the only thing that scrolls vertically, so this
       section is `flex: 1` with a `minHeight: 0` floor, the card fills what is
       left, and the rows scroll inside it. That is what keeps the headings and
       the pager on screen. */
    <section
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        /* The page gutter and the foot. The band above takes its sides from
           this and adds only its own two vertical paddings — the gutter is the
           chrome's own, so the title lines up with the brand and the
           destinations over it, and the card under it. */
        padding: withGutter(gutter, 0, 20),
      }}
    >
      {/* The title, what the list is showing and the two controls that change
          it, in the one row every destination opens with. The search and the
          button are children of that row rather than of a block beside the
          title, so the search can give up width before anything wraps. */}
      <PageHead
        title="Customers"
        caption={
          visible ? (
            <>
              {/* The count is the server's, not the page's: what the office is
                  asking about is the whole list, and the 25 on screen are not
                  the answer to it. */}
              {q ? `${st.custTotal} of ${st.custVisible}` : st.custVisible} customers
              {q ? ` matching “${q}”` : ''} · {inShop.length} jobs in the shop · {order}
            </>
          ) : null
        }
      >
        {visible ? (
          <input
            className="input"
            name="customerSearch"
            value={st.custQ}
            autoComplete={NO_AUTOFILL}
            onChange={(e) => st.setCustomerQuery(e.target.value)}
            placeholder="Customer, contact, phone or email"
            aria-label="Search customers"
            // The floor as the basis and the drawn width as the cap — the same
            // construction the jobs search uses, and for the same reason: a
            // basis of the drawn width wraps the row instead of shrinking.
            style={{
              marginLeft: 'auto',
              flex: '1 1 200px',
              maxWidth: 300,
              height: 44,
              fontSize: 16,
            }}
          />
        ) : null}
        {visible && office ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={st.openNewCustomer}
            style={{ height: 44 }}
          >
            Add customer
          </button>
        ) : null}
      </PageHead>

      {!visible ? (
        <Panel padding={20} style={{ maxWidth: 560 }}>
          <h2
            style={{
              fontSize: 16,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: '0 0 6px',
            }}
          >
            Not available to subcontractors
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
            You see the jobs you are on and the work they need. Customer names, contacts and sites
            stay with the office.
          </p>
        </Panel>
      ) : st.custListStatus === 'error' ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}
        >
          <p
            role="alert"
            style={{
              margin: 0,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--urgent-tint)',
              border: '1px solid var(--urgent)',
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {st.custListError}
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void loadCustomerList()}
          >
            Try again
          </button>
        </div>
      ) : (
        <div
          className="blueprint"
          style={{
            background: 'var(--surface)',
            padding: 0,
            flex: '1 1 auto',
            minHeight: 260,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
            <table className="table table-sticky" style={{ width: '100%', minWidth: 1020 }}>
              <thead>
                <tr>
                  <CustSortHeader column="name" style={{ paddingLeft: 16 }} />
                  <th>Type</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th style={{ textAlign: 'right' }}>Jobs in shop</th>
                  <th style={{ textAlign: 'right' }}>Items</th>
                  <CustSortHeader column="promised" />
                  <th style={{ paddingRight: 16, textAlign: 'right' }}>Total jobs</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => st.openCustomer(c.name)}
                  >
                    <td style={{ paddingLeft: 16, fontSize: 14, fontWeight: 600 }}>{c.name}</td>
                    <td>
                      <span style={typeTag(c.type === 'Trade')}>{c.type}</span>
                    </td>
                    <td style={{ fontSize: 13.5 }}>{orDash(c.contact)}</td>
                    {/* The number the office rings, in mono with the job codes
                        and the dimensions — it is read out digit by digit. */}
                    <td style={{ fontFamily: 'var(--font-data)', fontSize: 12.5 }}>
                      {orDash(c.phone)}
                    </td>
                    <td style={num}>{c.jobsInShop}</td>
                    <td style={{ ...num, color: 'var(--ink-muted)' }}>{c.itemsInShop}</td>
                    <td
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 13,
                        color: overdueDay(c.nextPromisedAt) ? 'var(--urgent)' : 'var(--ink)',
                      }}
                    >
                      {c.nextPromisedAt === null ? 'Nothing booked' : formatDay(c.nextPromisedAt)}
                    </td>
                    <td style={{ ...num, paddingRight: 16, color: 'var(--ink-muted)' }}>
                      {c.totalJobs}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {rows.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  padding: '20px 16px',
                  fontSize: 13.5,
                  color: 'var(--ink-muted)',
                }}
              >
                {st.custListStatus !== 'ready'
                  ? 'Reading the customer list…'
                  : q
                    ? `Nothing matches “${q}”. Clear the search to see all ${st.custVisible}.`
                    : office
                      ? 'No customers yet. Add the first one — a job belongs to a customer.'
                      : 'No customers yet. The office adds them.'}
              </p>
            ) : null}
          </div>

          <Pager
            page={st.custPage}
            pages={st.custPages}
            total={st.custTotal}
            shown={rows.length}
            size={st.custSize}
            busy={st.custListStatus === 'loading'}
            onPage={st.goToCustomerPage}
            onSize={st.setCustomerPageSize}
            empty="No customers on this page"
          />
        </div>
      )}
    </section>
  );
}

/** One customer: who they are, their jobs, and what the floor should know about
 *  them.
 *
 *  Their details render as text rather than as a form. Changing them is the
 *  office's job and goes through the same dialog that adds a customer — one
 *  place where a change is checked against the customers the shop already has,
 *  rather than two that could disagree. */
export function CustomerScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const office = isOffice(st);
  const cust = currentCustomer(st);
  // The jobs list is a colour chip, a code and stage, a title, a date and a
  // count — about 480px before the title starts being cut — and the panel
  // beside it wants its own 300. Below the sum the second column goes under
  // the first rather than both being squeezed off the side of the screen.
  const twoColumns = !useNarrowerThan(CUSTOMER_TWO_COL);

  if (!cust) {
    return (
      <section style={{ flex: 1, padding: withGutter(gutter, 20, 60) }}>
        <BackButton
          to="Customers"
          onClick={() => st.set('screen', 'customers')}
          style={{ marginBottom: 12, marginLeft: -8 }}
        />
        <Empty>
          {st.custStatus === 'ready'
            ? 'No customers yet. Add the first one from the customers list.'
            : 'Reading the customer…'}
        </Empty>
      </section>
    );
  }

  const jobs = st.jobs
    .filter((j) => j.customer === cust.name)
    .slice()
    .sort((a, b) =>
      // Soonest promise first, and a job nobody has promised last. Days are
      // ISO strings, so ordering them is ordering the days themselves.
      !scheduled(a) ? 1 : !scheduled(b) ? -1 : compareDays(a.promised, b.promised),
    );
  const live = jobs.filter((j) => !stageOf(st, j.stage)?.terminal);

  const fields = [
    { label: 'Contact', value: orDash(cust.contact) },
    { label: 'Phone', value: orDash(cust.phone) },
    { label: 'Email', value: orDash(cust.email) },
    { label: 'Their reference prefix', value: orDash(cust.ref) },
  ];

  const edit = () => st.openEditCustomer(cust);

  return (
    <section style={{ flex: 1, padding: withGutter(gutter, 20, 60) }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: twoColumns ? 'minmax(0,1.5fr) minmax(0,1fr)' : 'minmax(0,1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Panel padding={20}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
              <BackButton
                to="Customers"
                onClick={() => st.set('screen', 'customers')}
                style={{ marginLeft: -8, alignSelf: 'center' }}
              />
              <h1 style={{ fontSize: 26, margin: 0 }}>{cust.name}</h1>
              <span
                style={{ ...typeTag(cust.type === 'Trade'), fontSize: 10, letterSpacing: '0.1em' }}
              >
                {cust.type}
              </span>
              {office ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={edit}
                  style={{ height: 40, marginLeft: 'auto' }}
                >
                  Edit customer
                </button>
              ) : null}
            </div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)' }}>
              {[cust.contact, cust.phone, cust.email].filter(Boolean).join(' · ') ||
                'No contact details yet'}
              {cust.ref ? ` · their refs run ${cust.ref}-nnnn` : ''}
            </p>
          </Panel>

          <Panel>
            <PanelHead
              title="Jobs"
              meta={`${jobs.length} total · ${live.length} in the shop`}
              action={
                office ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => st.openNewJob(cust)}
                  >
                    New job
                  </button>
                ) : null
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {jobs.length === 0 ? (
                <Empty>
                  Nothing in the shop for {cust.name} yet. New jobs for them collect here.
                </Empty>
              ) : null}
              {jobs.map((j) => (
                <button
                  key={j.code}
                  type="button"
                  onClick={() => st.openJob(j.code)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '30px 1fr 150px 136px',
                    gap: 12,
                    alignItems: 'center',
                    padding: '11px 4px',
                    borderTop: '1px solid var(--line-soft)',
                    background: 'transparent',
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    minHeight: 48,
                    width: '100%',
                  }}
                >
                  <Swatch code={j.colour} size={30} />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{ fontFamily: 'var(--font-data)', fontSize: 12.5, fontWeight: 500 }}
                      >
                        {j.code}
                      </span>
                      <span className="label-xs" style={{ fontSize: 9, letterSpacing: '0.09em' }}>
                        {stageName(st, j.stage)}
                      </span>
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13.5,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {j.title}
                    </span>
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 12.5,
                      color: isOverdue(j, st.stages) ? 'var(--urgent)' : 'var(--ink)',
                    }}
                  >
                    {formatDay(j.promised)}
                  </span>
                  <span
                    style={{
                      textAlign: 'right',
                      fontFamily: 'var(--font-data)',
                      fontSize: 12,
                      color: 'var(--ink-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {j.items} items · {areaFor(j).toFixed(1)} m²
                  </span>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Panel>
            <PanelHead
              title="Contact"
              action={
                office ? (
                  <button type="button" className="btn btn-ghost" onClick={edit}>
                    Edit
                  </button>
                ) : null
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fields.map((f) => (
                <div key={f.label}>
                  <div className="label-xs" style={{ letterSpacing: '0.12em' }}>
                    {f.label}
                  </div>
                  <div style={{ fontSize: 14 }}>{f.value}</div>
                </div>
              ))}
            </div>
            {!office ? (
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--ink-muted)' }}>
                The office keeps customer details up to date.
              </p>
            ) : null}
          </Panel>

          <Panel>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
              <h2
                style={{
                  fontSize: 15,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: 0,
                }}
              >
                Notes
              </h2>
              <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                Applies to every job for them
              </span>
              {office ? (
                <span style={{ marginLeft: 'auto' }}>
                  <button type="button" className="btn btn-ghost" onClick={edit}>
                    Edit
                  </button>
                </span>
              ) : null}
            </div>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
              {cust.note || 'No notes on this customer yet.'}
            </p>
          </Panel>

          <Panel>
            <PanelHead
              title="Sites"
              meta={`${cust.sites.length}`}
              action={
                office ? (
                  <button type="button" className="btn btn-ghost" onClick={edit}>
                    Edit
                  </button>
                ) : null
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cust.sites.length === 0 ? (
                <Empty dashed={false}>
                  No sites yet — a site is where finished work goes back to.
                </Empty>
              ) : null}
              {cust.sites.map((s) => (
                <div
                  key={s}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5 }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'var(--dim)',
                      flex: 'none',
                    }}
                  />
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
}
